import React, { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** If true, fallback will show error message + component stack for debugging (when no custom fallback) */
  showDebugInFallback?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  copySuccess: boolean;
}

/** Decode minified React #185 for display (invalid hook call) */
function decodeReact185(message: string): string {
  if (message.includes("185") && message.includes("invariant")) {
    return "Invalid hook call. Hooks can only be called inside the body of a function component (see Rules of Hooks).";
  }
  return message;
}

/** Build full debug string for copy (pathname, search, taskId, message, stack, componentStack) */
function buildDebugString(error: Error | null, errorInfo: React.ErrorInfo | null): string {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const search = typeof window !== "undefined" ? window.location.search : "";
  const taskId = search ? new URLSearchParams(search).get("taskId") ?? null : null;
  const parts = [
    "pathname: " + pathname,
    "search: " + search,
    "taskId: " + (taskId ?? "(none)"),
    "",
    "error.message:",
    error?.message ?? "",
    "",
    "error.stack:",
    error?.stack ?? "",
    "",
    "componentStack:",
    errorInfo?.componentStack ?? "",
  ];
  return parts.join("\n");
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copySuccess: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    const pathname = typeof window !== "undefined" ? window.location.pathname : "";
    const search = typeof window !== "undefined" ? window.location.search : "";
    const taskId = search ? new URLSearchParams(search).get("taskId") ?? null : null;
    console.error("[ErrorBoundary] pathname:", pathname, "search:", search, "taskId:", taskId);
    console.error("[ErrorBoundary] error.message:", error.message);
    console.error("[ErrorBoundary] error.stack:", error.stack);
    console.error("[ErrorBoundary] componentStack:", errorInfo.componentStack);
  }

  handleCopyDebug = () => {
    const { error, errorInfo } = this.state;
    const text = buildDebugString(error, errorInfo);
    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copySuccess: true });
      setTimeout(() => this.setState({ copySuccess: false }), 2000);
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      const { error, errorInfo } = this.state;
      const showDebug = this.props.showDebugInFallback && (error || errorInfo);
      const pathname = typeof window !== "undefined" ? window.location.pathname : "";
      const search = typeof window !== "undefined" ? window.location.search : "";
      const taskId = search ? new URLSearchParams(search).get("taskId") ?? null : null;
      const decodedMessage = error ? decodeReact185(error.message) : "";
      return (
        <div className="p-4 max-w-2xl mx-auto space-y-3">
          <p className="text-destructive font-semibold">Something went wrong. Please refresh the page.</p>
          {error && (
            <>
              <p className="text-sm text-muted-foreground">
                Error: {decodedMessage}
              </p>
              {decodedMessage !== error.message && (
                <p className="text-xs text-muted-foreground">(Original: {error.message})</p>
              )}
            </>
          )}
          {showDebug && (
            <details className="text-left" open>
              <summary className="cursor-pointer text-sm font-medium text-muted-foreground">
                Debug info (pathname, search, taskId, message, stack, componentStack)
              </summary>
              <div className="mt-2 space-y-2">
                <pre className="p-3 bg-muted rounded text-xs overflow-auto whitespace-pre-wrap break-all max-h-80">
                  {buildDebugString(error, errorInfo)}
                </pre>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={this.handleCopyDebug}
                >
                  {this.state.copySuccess ? "Copied!" : "Copy"}
                </Button>
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
